/**
 * Special Collection Search Filter
 * 
 * This script filters out products from the special collection in search results
 * when the user doesn't have the correct access parameter.
 * 
 * Usage: Automatically runs on search pages to hide special collection products
 * unless the user has ?access=123 in the URL or has stored access in sessionStorage.
 */

(function() {
    'use strict';
    
    // Configuration
    const CONFIG = {
        accessParam: 'access',
        accessValue: '123',
        sessionKey: 'special_collection_access',
        specialCollectionHandle: 'special-collection',
        specialProductType: 'زيوت ملاكي', // Special collection product type
        productSelector: '.t4s-product',
        searchResultsSelector: '.t4s-products, .t4s_box_pr_grid'
    };
    
    /**
     * Check if the current URL has the special access parameter
     * @returns {boolean} True if access parameter matches expected value
     */
    function hasSpecialAccess() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(CONFIG.accessParam) === CONFIG.accessValue;
    }
    
    /**
     * Check if special access is stored in sessionStorage
     * @returns {boolean} True if access is stored
     */
    function hasStoredAccess() {
        try {
            return sessionStorage.getItem(CONFIG.sessionKey) === 'true';
        } catch (e) {
            console.warn('Could not read sessionStorage:', e);
            return false;
        }
    }
    
    /**
     * Set the special access flag in sessionStorage
     */
    function setSpecialAccess() {
        try {
            sessionStorage.setItem(CONFIG.sessionKey, 'true');
        } catch (e) {
            console.warn('Could not set sessionStorage:', e);
        }
    }
    
    /**
     * Check if a product belongs to the special collection
     * @param {HTMLElement} productElement - The product element to check
     * @returns {boolean} True if product is from special collection
     */
    function isSpecialCollectionProduct(productElement) {
        console.log('Special Collection Filter: Checking product element:', productElement);
        
        // Method 1: Check data-product-options attribute for product_type (MOST RELIABLE)
        const productOptionsData = productElement.getAttribute('data-product-options');
        if (productOptionsData) {
            try {
                const productOptions = JSON.parse(productOptionsData);
                console.log('Special Collection Filter: Product options data:', productOptions);
                
                // Check if product_type matches special collection type
                if (productOptions.product_type === CONFIG.specialProductType) {
                    console.log('Special Collection Filter: Found special collection via product_type:', productOptions.product_type);
                    return true;
                }
                
                // Also check type field (alternative naming)
                if (productOptions.type === CONFIG.specialProductType) {
                    console.log('Special Collection Filter: Found special collection via type:', productOptions.type);
                    return true;
                }
                
                console.log('Special Collection Filter: Product type is:', productOptions.product_type || productOptions.type || 'not specified');
            } catch (e) {
                console.log('Special Collection Filter: Error parsing product options:', e);
            }
        }
        
        // Method 2: Check legacy data-product attribute for product_type
        const productData = productElement.getAttribute('data-product');
        if (productData) {
            try {
                const product = JSON.parse(productData);
                console.log('Special Collection Filter: Legacy product data:', product);
                
                if (product.product_type === CONFIG.specialProductType || product.type === CONFIG.specialProductType) {
                    console.log('Special Collection Filter: Found special collection via legacy product_type');
                    return true;
                }
            } catch (e) {
                console.log('Special Collection Filter: Error parsing legacy product data:', e);
            }
        }
        
        // Method 3: Check for product type in any data attribute that might contain it
        const allDataAttributes = Array.from(productElement.attributes)
            .filter(attr => attr.name.startsWith('data-'))
            .map(attr => ({ name: attr.name, value: attr.value }));
        
        for (const attr of allDataAttributes) {
            if (attr.value.includes(CONFIG.specialProductType)) {
                console.log(`Special Collection Filter: Found special product type in ${attr.name}:`, attr.value);
                return true;
            }
        }
        
        // Method 4: Check product title for the special product type text
        const productTitle = productElement.querySelector('.t4s-product-title, .product-title, h3, h4');
        if (productTitle) {
            const titleText = productTitle.textContent;
            console.log('Special Collection Filter: Product title:', titleText);
            
            if (titleText.includes(CONFIG.specialProductType)) {
                console.log('Special Collection Filter: Found special collection via product title');
                return true;
            }
        }
        
        // Method 5: Check if any text content in the product element contains the special type
        const productText = productElement.textContent || '';
        if (productText.includes(CONFIG.specialProductType)) {
            console.log('Special Collection Filter: Found special collection via product text content');
            return true;
        }
        
        // Fallback methods (keeping for compatibility)
        
        // Method 6: Check product links for collection references
        const productLinks = productElement.querySelectorAll('a[href*="/products/"]');
        for (const link of productLinks) {
            const href = link.getAttribute('href');
            if (href && href.includes('/collections/' + CONFIG.specialCollectionHandle)) {
                console.log('Special Collection Filter: Found special collection in product link');
                return true;
            }
        }
        
        // Method 7: Check for data attributes that might indicate special collection
        const dataCollection = productElement.getAttribute('data-collection');
        if (dataCollection && dataCollection.includes(CONFIG.specialCollectionHandle)) {
            console.log('Special Collection Filter: Found special collection in data-collection attribute');
            return true;
        }
        
        // Method 8: Check for special collection class or data attribute
        if (productElement.classList.contains('special-collection') || 
            productElement.classList.contains(CONFIG.specialCollectionHandle) ||
            productElement.hasAttribute('data-special-collection')) {
            console.log('Special Collection Filter: Found special collection via class/attribute');
            return true;
        }
        
        console.log('Special Collection Filter: Product is NOT from special collection');
        return false;
    }
    
    /**
     * Hide products from special collection
     */
    function hideSpecialCollectionProducts() {
        console.log('Special Collection Filter: Hiding special collection products...');
        
        const searchResults = document.querySelectorAll(CONFIG.searchResultsSelector);
        let hiddenCount = 0;
        
        searchResults.forEach(container => {
            const products = container.querySelectorAll(CONFIG.productSelector);
            
            products.forEach(product => {
                if (isSpecialCollectionProduct(product)) {
                    product.style.display = 'none';
                    product.classList.add('special-collection-hidden');
                    hiddenCount++;
                    console.log('Special Collection Filter: Hidden product:', product);
                }
            });
        });
        
        console.log('Special Collection Filter: Hidden', hiddenCount, 'special collection products');
    }
    
    /**
     * Show all products (including special collection)
     */
    function showAllProducts() {
        console.log('Special Collection Filter: Showing all products...');
        
        // Restore any previously hidden products by this script
        const hiddenProducts = document.querySelectorAll('.special-collection-hidden');
        console.log('Special Collection Filter: Found', hiddenProducts.length, 'previously hidden products');
        
        hiddenProducts.forEach(product => {
            product.style.display = '';
            product.classList.remove('special-collection-hidden');
            console.log('Special Collection Filter: Restored product:', product);
        });
    }
    
    /**
     * Filter search results based on access
     */
    function filterSearchResults() {
        console.log('Special Collection Filter: Checking access...');
        console.log('URL Access:', hasSpecialAccess());
        console.log('Stored Access:', hasStoredAccess());
        
        if (hasSpecialAccess()) {
            console.log('Special Collection Filter: URL access granted, setting session and showing all products');
            setSpecialAccess();
            showAllProducts();
        } else if (hasStoredAccess()) {
            console.log('Special Collection Filter: Session access granted, showing all products');
            showAllProducts();
        } else {
            console.log('Special Collection Filter: No access, hiding special collection products');
            hideSpecialCollectionProducts();
        }
    }
    
    /**
     * Initialize the search filter
     */
    function initSearchFilter() {
        console.log('Special Collection Filter: Initializing on path:', window.location.pathname);
        
        // Only run on search pages
        if (!window.location.pathname.includes('/search')) {
            console.log('Special Collection Filter: Not a search page, skipping');
            return;
        }
        
        console.log('Special Collection Filter: Search page detected, waiting for results...');
        
        // Wait for search results to load
        const checkForResults = () => {
            const searchResults = document.querySelector(CONFIG.searchResultsSelector);
            if (searchResults) {
                console.log('Special Collection Filter: Search results found, applying filter');
                filterSearchResults();
            } else {
                console.log('Special Collection Filter: Search results not found yet, retrying...');
                // Retry after a short delay
                setTimeout(checkForResults, 100);
            }
        };
        
        checkForResults();
    }
    
    /**
     * Handle AJAX search updates
     */
    function handleAjaxSearch() {
        // Listen for AJAX search updates
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    const addedNodes = Array.from(mutation.addedNodes);
                    const hasSearchResults = addedNodes.some(node => 
                        node.nodeType === Node.ELEMENT_NODE && 
                        (node.matches && node.matches(CONFIG.searchResultsSelector)) ||
                        (node.querySelector && node.querySelector(CONFIG.searchResultsSelector))
                    );
                    
                    if (hasSearchResults) {
                        setTimeout(filterSearchResults, 50);
                    }
                }
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
    
    /**
     * Debug function to analyze product structure on the page
     */
    function debugProductStructure() {
        console.log('=== SPECIAL COLLECTION DEBUG ===');
        console.log('Current URL:', window.location.href);
        console.log('Is search page:', window.location.pathname.includes('/search'));
        
        const searchResults = document.querySelectorAll(CONFIG.searchResultsSelector);
        console.log('Search result containers found:', searchResults.length);
        
        searchResults.forEach((container, index) => {
            console.log(`Container ${index + 1}:`, container);
            const products = container.querySelectorAll(CONFIG.productSelector);
            console.log(`Products in container ${index + 1}:`, products.length);
            
            products.forEach((product, productIndex) => {
                console.log(`--- Product ${productIndex + 1} ---`);
                console.log('Element:', product);
                console.log('Classes:', product.className);
                console.log('Data attributes:', Object.fromEntries(
                    Array.from(product.attributes)
                    .filter(attr => attr.name.startsWith('data-'))
                    .map(attr => [attr.name, attr.value])
                ));
                
                const productLinks = product.querySelectorAll('a[href*="/products/"]');
                console.log('Product links:', Array.from(productLinks).map(link => link.href));
                
                const title = product.querySelector('.t4s-product-title, .product-title, h3, h4');
                if (title) {
                    console.log('Product title:', title.textContent.trim());
                }
                
                // Check if this product would be detected as special collection
                const isSpecial = isSpecialCollectionProduct(product);
                console.log('Is special collection:', isSpecial);
                console.log('------------------------');
            });
        });
        
        console.log('=== END DEBUG ===');
    }
    
    // Expose debug function globally for manual testing
    window.debugSpecialCollection = debugProductStructure;
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initSearchFilter();
            handleAjaxSearch();
        });
    } else {
        initSearchFilter();
        handleAjaxSearch();
    }
    
    // Handle navigation events
    window.addEventListener('popstate', initSearchFilter);
    
})();

